import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function Generate() {
  const [progress, setProgress] = useState(5);
  const [step, setStep] = useState('Recherche des informations…');
  const [error, setError] = useState(null);
  const started = useRef(false);
  const nav = useNavigate();

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const raw = sessionStorage.getItem('autoradko_topic');
    const mediaRaw = sessionStorage.getItem('autoradko_media');
    if (!raw || !mediaRaw) { nav('/'); return; }
    const topic = JSON.parse(raw);
    const media = JSON.parse(mediaRaw);

    // Simulated progress while AI works
    const messages = [
      { t: 800, p: 20, s: 'Analyse du sujet…' },
      { t: 2500, p: 45, s: 'Recherche de sources complémentaires…' },
      { t: 6000, p: 65, s: `Rédaction selon le style de ${media.name}…` },
      { t: 11000, p: 85, s: 'Recherche d\'une image…' },
      { t: 16000, p: 92, s: 'Finalisation…' },
    ];
    const timers = messages.map((m) => setTimeout(() => { setProgress(m.p); setStep(m.s); }, m.t));

    (async () => {
      try {
        const extraInstruction = sessionStorage.getItem('autoradko_extra_instruction');
        const { data } = await api.post('/articles/generate', {
          topic,
          media_id: media.id,
          ...(extraInstruction ? { extra_instruction: extraInstruction } : {}),
        });
        // Cleanup ad-hoc instruction so it doesn't leak into the next generation
        sessionStorage.removeItem('autoradko_extra_instruction');
        setProgress(100);
        setStep('Article prêt !');
        setTimeout(() => nav(`/preview/${data.id}`), 400);
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Erreur lors de la génération';
        setError(msg);
        toast.error(msg);
      } finally {
        timers.forEach(clearTimeout);
      }
    })();
    return () => timers.forEach(clearTimeout);
  }, [nav]);

  return (
    <div className="mobile-shell">
      <div className="flex flex-col items-center justify-center min-h-[85vh] px-8 text-center">
        <img
          src="/brand/autoradko-icon.png"
          alt="Auto Radko"
          className="w-44 h-44 mb-8 select-none drop-shadow-xl rounded-3xl"
          style={{ animation: 'fadeInUp 0.5s ease-out, pulseGlow 2.4s ease-in-out infinite alternate' }}
          draggable={false}
          data-testid="gen-logo"
        />
        <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="gen-title">Génération de l'article</h1>
        <p className="text-sm text-slate-500 max-w-xs mb-8" data-testid="gen-step">
          {error ? 'Une erreur est survenue.' : step}
        </p>

        {!error && (
          <div className="w-full max-w-xs">
            <div className="w-full h-3 bg-blue-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
                data-testid="gen-progress"
              />
            </div>
            <div className="text-xs text-slate-500 mt-2 font-semibold">{progress}%</div>
          </div>
        )}

        {error && (
          <div className="mt-4 space-y-3 w-full max-w-xs">
            <div className="text-sm text-red-600">{error}</div>
            <button
              onClick={() => nav('/')}
              className="w-full border-2 border-slate-200 text-slate-700 font-semibold py-3 rounded-xl hover:bg-slate-50 active:scale-95 transition"
              data-testid="btn-back-feed"
            >
              Retour au feed
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
